/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback, useRef, useEffect, useMemo } from '@web-stories-wp/react';
import { EditorState, RichUtils } from 'draft-js';

/**
 * Internal dependencies
 */
import formatters from './formatters';
import { removeListFormatting } from './util';

function useSelectionManipulation(editorState, setEditorState) {
  const lastKnownState = useRef(null);
  const lastKnownSelection = useRef(null);
  useEffect(() => {
    lastKnownState.current = editorState;
    if (!editorState) {
      lastKnownSelection.current = null;
    } else if (editorState.getSelection().hasFocus) {
      lastKnownSelection.current = editorState.getSelection();
    }
  }, [editorState]);

  const updateWhileUnfocused = useCallback(
    (updater, shouldForceFocus = true) => {
      const oldState = lastKnownState.current;
      if (!oldState) {
        return;
      }

      // A toolbar or popover action can run before this editor has ever held
      // focus, so there might not be a cached focused selection yet. Draft's
      // current selection is still valid in that case and is the safest
      // fallback for applying the requested formatting.
      const selection = lastKnownSelection.current || oldState.getSelection();
      const workingState =
        shouldForceFocus && selection
          ? EditorState.forceSelection(oldState, selection)
          : oldState;
      const newState = updater(workingState);
      setEditorState(newState);
    },
    [setEditorState]
  );

  const getSetterName = useCallback(
    (setterName) => `${setterName}InSelection`,
    []
  );

  const getSetterCallback = useCallback(
    (setter, autoFocus) =>
      (...args) =>
        updateWhileUnfocused((state) => setter(state, ...args), autoFocus),
    [updateWhileUnfocused]
  );

  const selectionFormatters = useMemo(
    () =>
      formatters.reduce(
        (aggr, { setters, autoFocus }) => ({
          ...aggr,
          ...Object.fromEntries(
            Object.entries(setters).map(([key, setter]) => [
              getSetterName(key),
              getSetterCallback(setter, autoFocus),
            ])
          ),
        }),
        {}
      ),
    [getSetterName, getSetterCallback]
  );

  const listFormatters = useMemo(
    () => ({
      toggleUnorderedListInSelection: () =>
        updateWhileUnfocused((state) =>
          RichUtils.toggleBlockType(state, 'unordered-list-item')
        ),
      toggleOrderedListInSelection: () =>
        updateWhileUnfocused((state) =>
          RichUtils.toggleBlockType(state, 'ordered-list-item')
        ),
      removeListInSelection: () => updateWhileUnfocused(removeListFormatting),
    }),
    [updateWhileUnfocused]
  );

  return {
    ...selectionFormatters,
    ...listFormatters,
  };
}

export default useSelectionManipulation;
